import { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FinancialWarningsProvider } from 'contexts/FinancialWarningsContext';
import { BusinessPageToolLayout } from 'layouts/BusinessPageToolLayout';
import { OnePageExampleSwitch, OnePagePreviewLayout } from 'layouts/OnePagePreviewLayout';
import {
  OnePageStyleSelector,
  OnePageStyleSelectorMobile,
} from 'layouts/OnePagePreviewLayout/components/OnePageStyleSelector';
import { useBusinessToolSessionStore, useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessPlanType,
  getBusinessToolMode,
  getSetActiveSection,
  getSetActiveSubSection,
} from 'store/useBusinessToolStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { Loader } from 'components/_basic/Loader/Loader';
import { NavigateToErrorPage } from 'components/_basic/NavigateToErrorPage';
import { BusinessToolForm } from 'components/BusinessToolForm';
import { AIFormLoader } from 'components/BusinessToolForm/components/AIFormLoader';
import { CalculationsObserver } from 'components/BusinessToolForm/components/CalculationsObserver';
import { FormChangeObserver } from 'components/BusinessToolForm/components/FormChangeObserver';
import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';
import { useHeaderLogic } from 'components/Header/HeaderLogicProvider';
import { OnePagePreviewContainer } from 'components/OnePageTemplates';

import { getActiveFormSection, getActiveFormSubSection } from 'helpers/setActiveFormSection';

import { BusinessToolHeader } from './BusinessToolHeader';
import { BusinessToolNavigation } from './BusinessToolNavigation';
import { AiContentGenerationObserver } from './components/AiContentGenerationObserver';
import { BusinessToolAiProvider } from './components/BusinessToolAiProvider';
import { BusinessToolFormContainer } from './components/BusinessToolFormContainer';
import { ClearBusinessToolObserver } from './components/ClearBusinessToolObserver';
import { DeviceTypeObserver } from './components/DeviceTypeObserver';
import { EditModeComponent } from './components/EditModeComponent/EditModeComponent';
import { OfferToSaveTheDocument } from './components/OfferToSaveTheDocument';
import { PreviewModeComponent } from './components/PreviewModeComponent/PreviewModeComponent';
import { useBusinessToolPageLogic } from './hooks/useBusinessToolPageLogic';
import { PdfPreview } from './PdfPreview';
import { IBusinessToolProps } from './types';
import { checkIsOnePagePreviewMode, checkIsPreviewMode } from './utils';

export const BusinessToolPage: FC = () => {
  const { id: businessPlanId = '' } = useParams();
  const { isAuthenticated } = useAuthenticated();

  const { isLoading, error, initialValues } = useBusinessToolPageLogic({ businessPlanId });

  if (isLoading) {
    return <Loader />;
  }

  if (error && isAuthenticated) {
    return <NavigateToErrorPage errorCode="404" replace />;
  }

  if (error?.status === 403) {
    return <NavigateToErrorPage errorCode="404" replace />;
  }

  return (
    <>
      <Helmet>
        <script type="text/javascript" defer>
          {`
            window.dataLayer = window.dataLayer || [];
            let userId = '';
            try {
              const userStore = localStorage.getItem('user-store');
              if (userStore) {
                const parsed = JSON.parse(userStore);
                userId = parsed?.state?.authUser?.id || '';
                dataLayer.push({ user_id: userId });
              }
            } catch (e) {
              console.error('Failed to parse user-store:', e);
            }
          `}
        </script>
      </Helmet>
      <BusinessTool initialValues={initialValues} />
      <OfferToSaveTheDocument />
      <ClearBusinessToolObserver />
      <DeviceTypeObserver />
    </>
  );
};

const BusinessTool: React.FC<IBusinessToolProps> = ({ initialValues }) => {
  const businessToolMode = useBusinessToolSessionStore(getBusinessToolMode);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const { isAuthenticated } = useAuthenticated();

  const { hasSubscription } = useHeaderLogic();

  const setActiveSection = useBusinessToolStore(getSetActiveSection);
  const setActiveSubSection = useBusinessToolStore(getSetActiveSubSection);

  useEffect(() => {
    setActiveSection(getActiveFormSection(businessPlanType));
    setActiveSubSection(getActiveFormSubSection(businessPlanType));
  }, []);

  const methods = useForm<IBusinessToolForm>({
    // TODO fix the validation error
    // resolver: yupResolver<IBusinessToolForm>(validationSchema),
    defaultValues: initialValues,
  });

  const isPreviewMode = checkIsPreviewMode(businessToolMode);
  const isOnePagePreviewMode = checkIsOnePagePreviewMode(businessToolMode, businessPlanType);

  return (
    <FormProvider {...methods}>
      <FinancialWarningsProvider>
      <BusinessToolAiProvider>
        {isOnePagePreviewMode ? (
          <BusinessToolNavigation>
            {({ modeSwitch }) => (
              <OnePagePreviewLayout
                header={<BusinessToolHeader />}
                onePagePreview={<OnePagePreviewContainer />}
                onePagePreviewControls={
                  <>
                    <OnePageStyleSelector />
                    <OnePageExampleSwitch />
                  </>
                }
                onePagePreviewControlsMobile={
                  <>
                    <OnePageStyleSelectorMobile />
                    <OnePageExampleSwitch />
                  </>
                }
                modeSwitch={modeSwitch}
              />
            )}
          </BusinessToolNavigation>
        ) : (
          <BusinessToolNavigation>
            {(navigationComponents) => (
              <BusinessPageToolLayout
                isPreviewMode={isPreviewMode}
                header={<BusinessToolHeader />}
                form={
                  <BusinessToolFormContainer>
                    <AIFormLoader />
                    {!isPreviewMode && <BusinessToolForm />}
                    {isPreviewMode && <PdfPreview />}
                  </BusinessToolFormContainer>
                }
                aside={isPreviewMode ? <PreviewModeComponent /> : <EditModeComponent />}
                {...navigationComponents}
              />
            )}
          </BusinessToolNavigation>
        )}

        <AiContentGenerationObserver />
      </BusinessToolAiProvider>

      {(!isAuthenticated || hasSubscription) && <FormChangeObserver />}
      <CalculationsObserver />
      </FinancialWarningsProvider>
    </FormProvider>
  );
};
